/**
 * CausalFlowDiagram - Horizontal Causal Chain Visualization
 * ============================================================================
 * DC-913: Visual representation of causal analysis for recommendations
 *
 * Displays a horizontal flow diagram showing:
 * Problem → Root Cause → Intervention → Expected Outcome
 *
 * With counterfactual warning banner and confidence indicator.
 *
 * Design: Option B - Horizontal Flow Boxes
 * Reference: Judea Pearl's Ladder of Causation
 *
 * @version 1.0.0
 * @created 2026-01-10
 * ============================================================================
 */

import React from 'react';
import type { CausalAnalysis } from '../types/bridgeIntelligence';
import '../styles/causal-flow-diagram.css';

export interface CausalFlowDiagramProps {
  /** Causal analysis data from recommendation */
  causalAnalysis: CausalAnalysis;
  /** Compact mode for smaller layouts */
  compact?: boolean;
  /** Optional custom className */
  className?: string;
}

// ============================================================================
// Helper Components
// ============================================================================

interface FlowBoxProps {
  label: string;
  content: string;
  variant: 'problem' | 'cause' | 'intervention' | 'outcome';
  compact?: boolean;
}

const FlowBox: React.FC<FlowBoxProps> = ({ label, content, variant, compact }) => {
  return (
    <div className={`causal-flow-box causal-flow-box--${variant} ${compact ? 'causal-flow-box--compact' : ''}`}>
      <span className="causal-flow-box__label">{label}</span>
      <p className="causal-flow-box__content">{content}</p>
    </div>
  );
};

const FlowArrow: React.FC<{ compact?: boolean }> = ({ compact }) => (
  <div className={`causal-flow-arrow ${compact ? 'causal-flow-arrow--compact' : ''}`}>
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
    </svg>
  </div>
);

interface ConfidenceBadgeProps {
  confidence: 'high' | 'medium' | 'low';
}

const ConfidenceBadge: React.FC<ConfidenceBadgeProps> = ({ confidence }) => {
  const labels = {
    high: 'High Confidence',
    medium: 'Medium Confidence',
    low: 'Low Confidence',
  };

  return (
    <span className={`causal-confidence-badge causal-confidence-badge--${confidence}`}>
      {labels[confidence]}
    </span>
  );
};

// ============================================================================
// Main Component
// ============================================================================

export const CausalFlowDiagram: React.FC<CausalFlowDiagramProps> = ({
  causalAnalysis,
  compact = false,
  className = '',
}) => {
  const {
    root_cause,
    causal_chain,
    intervention,
    expected_outcome,
    counterfactual,
    confidence,
  } = causalAnalysis;

  // Extract problem from causal_chain if available
  const problem = causal_chain.length > 0 ? causal_chain[0] : 'Issue identified';

  return (
    <div
      className={`causal-flow-diagram ${compact ? 'causal-flow-diagram--compact' : ''} ${className}`}
      role="region"
      aria-label="Causal Analysis Diagram"
    >
      {/* Header with confidence badge */}
      <div className="causal-flow-header">
        <h5 className="causal-flow-title">Causal Analysis</h5>
        <ConfidenceBadge confidence={confidence} />
      </div>

      {/* Horizontal Flow Boxes */}
      <div className="causal-flow-boxes">
        <FlowBox
          label="PROBLEM"
          content={problem}
          variant="problem"
          compact={compact}
        />
        <FlowArrow compact={compact} />
        <FlowBox
          label="ROOT CAUSE"
          content={root_cause}
          variant="cause"
          compact={compact}
        />
        <FlowArrow compact={compact} />
        <FlowBox
          label="FIX"
          content={intervention}
          variant="intervention"
          compact={compact}
        />
        <FlowArrow compact={compact} />
        <FlowBox
          label="OUTCOME"
          content={expected_outcome}
          variant="outcome"
          compact={compact}
        />
      </div>

      {/* Counterfactual Warning Banner */}
      {counterfactual && (
        <div className="causal-counterfactual-banner">
          <svg
            className="causal-counterfactual-icon"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
            />
          </svg>
          <span className="causal-counterfactual-text">
            <strong>Without this fix:</strong> {counterfactual}
          </span>
        </div>
      )}
    </div>
  );
};

export default CausalFlowDiagram;
